import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Grid,
    CircularProgress
} from '@mui/material';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
    ResponsiveContainer
} from 'recharts';
import { reportService } from '../../../services/api';
import SkillsProgressionChart from './SkillsProgressionChart';
import RoleReadinessChart from './RoleReadinessChart';
import MostSearchedTerms from './MostSearchedTerms';
import ComplianceChart from './ComplianceChart';

const ReportsDashboard = () => {
    const [skillsData, setSkillsData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [dashboardStats, setDashboardStats] = useState({
        activeLearners: 0,
        skillsAcquired: 0
    });

    const fetchSkillsData = async () => {
        try {
            setLoading(true);
            // Reuse existing API call but limit to 6 records
            const response = await reportService.getSkillGraphSummary({
                page: 0,
                limit: 6,
                search: ''
            });

            if (response.data.success) {
                // Transform data for chart
                const transformedData = response.data.data.map(skill => ({
                    name: skill.skillName,
                    completion: skill.avgCompletionPercent
                }));
                setSkillsData(transformedData);
            }
        } catch (error) {
            console.error('Error fetching skills data:', error);
            setError('Failed to load skills data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const fetchDashboardData = async () => {
            try {
                const [statsResponse, searchResponse] = await Promise.all([
                    reportService.getDashboardStats(),
                    reportService.getSearchStats()
                ]);

                if (statsResponse.data.success) {
                    setDashboardStats(prev => ({
                        ...prev,
                        ...statsResponse.data.data
                    }));
                }

                if (searchResponse.data.success) {
                    setDashboardStats(prev => ({
                        ...prev,
                        searchMatchPercentage: searchResponse.data.data.overallMatchPercentage
                    }));
                }
            } catch (error) {
                console.error('Error fetching dashboard data:', error);
            }
        };

        fetchDashboardData();
    }, []);

    useEffect(() => {
        fetchSkillsData();
    }, []);

    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <Box
                    sx={{
                        bgcolor: 'background.paper',
                        p: 1,
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 1
                    }}
                >
                    <Typography variant="body2">{label}</Typography>
                    <Typography variant="body2" color="primary">
                        {`Completion: ${payload[0].value}%`}
                    </Typography>
                </Box>
            );
        }
        return null;
    };

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom>
                Reports Dashboard
            </Typography>
            <Grid container spacing={3}>
                <Grid item xs={6} md={3}  sx={{ mb: 3 }}>
                    <Card>
                        <CardContent>
                            <Box>
                                <Typography variant="h6">
                                    Active Learners
                                </Typography>
                                <Typography variant="h1" color="primary">
                                    {dashboardStats.activeLearners}
                                </Typography>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>                
                <Grid item xs={6} md={3}>
                    <Card>
                        <CardContent>
                            <Box>
                                <Typography variant="h6">
                                    Skills Acquired
                                </Typography>
                                <Typography variant="h1" color="primary">
                                    {dashboardStats.skillsAcquired}
                                </Typography>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={6} md={3}>
                    <Card>
                        <CardContent>
                            <Box>
                                <Typography variant="h6">
                                    Search  Match
                                </Typography>
                                <Typography variant="h1" color="primary">
                                    {dashboardStats.searchMatchPercentage || 0}%
                                </Typography>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={6} md={3}>
                    <Card>
                        <CardContent>
                            <Box>
                                <Typography variant="h6">
                                    Adoption Rate
                                </Typography>
                                <Typography variant="h1" color="primary">
                                    85%
                                </Typography>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
            <Grid container spacing={3}>
                {/* Skills Progression Chart */}
                <Grid item xs={12} md={6}>
                    <SkillsProgressionChart />
                </Grid>
                <Grid item xs={12} md={6}>
                     <MostSearchedTerms />
                </Grid>                
                <Grid item xs={12} md={6}>
                    <RoleReadinessChart />
                </Grid>
                <Grid item xs={12} md={6}>
                    <ComplianceChart />
                </Grid> 
            </Grid>
        </Box>
    );
};

export default ReportsDashboard;